/**
 * ShareDialog — Modal for sharing a custom report template with other users.
 * Shows checkboxes for each colleague in the org (excludes self).
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { customReportService } from '../../services/api/customReportService';
import type { ClientUser } from '../../types/customReports';

interface ShareDialogProps {
  isOpen: boolean;
  clientId: string;
  uid: string;
  templateId: string;
  templateName: string;
  currentSharedWith: string[];
  onClose: () => void;
  onSaved: (sharedWith: string[]) => void;
}

const ShareDialog: React.FC<ShareDialogProps> = ({
  isOpen,
  clientId,
  uid,
  templateId,
  templateName,
  currentSharedWith,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<ClientUser[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set(currentSharedWith));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setSelected(new Set(currentSharedWith));
    setError(null);
    setLoading(true);
    customReportService.getClientUsers(clientId).then(data => {
      // Exclude self
      setUsers(data.filter(u => u.uid !== uid));
      setLoading(false);
    }).catch(err => {
      console.error('Error loading client users:', err);
      setError(t('customReports.share.loadError', 'Failed to load users'));
      setLoading(false);
    });
  }, [isOpen, clientId, uid, currentSharedWith]);

  if (!isOpen) return null;

  const toggleUser = (userUid: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(userUid)) {
        next.delete(userUid);
      } else {
        next.add(userUid);
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const sharedWith = Array.from(selected);
      await customReportService.shareTemplate(clientId, uid, templateId, sharedWith);
      onSaved(sharedWith);
    } catch (err: any) {
      setError(err.message || t('customReports.share.saveError', 'Failed to update sharing'));
      setSaving(false);
    }
  };

  const handleUnshareAll = async () => {
    setSaving(true);
    setError(null);
    try {
      await customReportService.shareTemplate(clientId, uid, templateId, []);
      onSaved([]);
    } catch (err: any) {
      setError(err.message || t('customReports.share.unshareError', 'Failed to unshare'));
      setSaving(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="alert-modal-backdrop" onClick={handleBackdropClick}>
      <div className="alert-modal alert-modal-info" style={{ maxWidth: '480px' }}>
        <div className="alert-modal-header">
          <h3 className="alert-modal-title">{t('customReports.share.title', 'Share "{{name}}"', { name: templateName })}</h3>
          <button className="alert-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="alert-modal-content">
          {error && <div className="cr-error" style={{ marginBottom: '0.75rem' }}>{error}</div>}
          {loading ? (
            <p style={{ color: 'var(--text-secondary)' }}>{t('customReports.share.loadingUsers', 'Loading users...')}</p>
          ) : users.length === 0 ? (
            <p style={{ color: 'var(--text-secondary)' }}>{t('customReports.share.noUsers', 'No other users in your organization.')}</p>
          ) : (
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {users.map(u => (
                <label
                  key={u.uid}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.4rem 0',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(u.uid)}
                    onChange={() => toggleUser(u.uid)}
                    style={{ width: '14px', height: '14px', margin: 0, cursor: 'pointer' }}
                  />
                  <span style={{ color: 'var(--text-primary)' }}>{u.displayName}</span>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{u.email}</span>
                </label>
              ))}
            </div>
          )}
        </div>
        <div className="alert-modal-actions">
          {currentSharedWith.length > 0 && (
            <button
              className="alert-modal-button danger"
              onClick={handleUnshareAll}
              disabled={saving}
              style={{ marginRight: 'auto' }}
            >
              {t('customReports.share.unshareAll', 'Unshare All')}
            </button>
          )}
          <button className="alert-modal-button" onClick={onClose} disabled={saving}>
            {t('customReports.buttons.cancel', 'Cancel')}
          </button>
          <button
            className="alert-modal-button primary"
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? t('customReports.buttons.saving', 'Saving...') : t('customReports.share.save', 'Save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareDialog;
